<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { beforeUpdate } from 'svelte';
	import { format } from 'svelte-i18n';
	import { real_id } from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { postReq } from '../../../services/http';
	import { gen1CloudReq } from 'src/services/gen_1_reqs';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	import { req_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';

	//Design system components
	import {
		DeviceButton,
		DoubleDeviceButton,
		Text,
		Icon,
		toast,
		DoubleDeviceButtonV2,
		DeviceButtonV2,
		type DeviceButtonV2PropsTypes,
		type DoubleDeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import { hasControlRights } from 'src/helpers/user_helpers';
	import { formatTargetValue } from 'src/helpers/trv_helpers';

	let className = '';
	export { className as class };
	export let device, status, settings;

	export let useV2Components = false;
	export let devBtnsV2Props: {
		deviceButtonV2?: Partial<DeviceButtonV2PropsTypes>;
		doubleDeviceButtonV2?: Partial<DoubleDeviceButtonV2PropsTypes>;
	} = {
		deviceButtonV2: { size: 's' },
		doubleDeviceButtonV2: { size: 's' },
	};

	let manual = !settings?.thermostats?.[0]?.target_t?.enabled,
		calibrated = status?.thermostats?.[0]?.pos !== -1,
		unit = manual ? '%' : settings?.thermostats?.[0]?.target_t?.units,
		sendReq = false,
		reqLocked = false,
		isLoading = false,
		disabled = false,
		long = false,
		windowOpen = status?.thermostats?.[0]?.window_open,
		min,
		max,
		value_to_show,
		pinLocked,
		showModal = false,
		callbackValue;
	let value = manual
			? Number(Math.ceil(status?.thermostats?.[0]?.pos))
			: settings?.thermostats?.[0]?.target_t?.value,
		cacheValue = value;

	$: hasRights = hasControlRights(device);

	beforeUpdate(() => {
		manual = !settings?.thermostats?.[0]?.target_t?.enabled;
		unit = manual ? '%' : settings?.thermostats?.[0]?.target_t?.units;
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;
		if (unit === 'F') {
			min = 40;
			max = 87;
		} else {
			min = 4;
			max = 31;
		}
		calibrated = status?.thermostats?.[0]?.pos !== -1;
		windowOpen = status?.thermostats?.[0]?.window_open; //This is a newly supported setting.
		if (windowOpen && value === cacheValue) {
			//Here we get desired temperature if window is opened.
			value = settings?.thermostats?.[0]?.target_t?.value_op; //this is not needed because even devices local web displays target_t.value temperature
			cacheValue = value;
		} else if (!windowOpen && value === cacheValue) {
			value = manual
				? Number(Math.ceil(status?.thermostats?.[0]?.pos))
				: settings?.thermostats?.[0]?.target_t?.value;
			cacheValue = value;
		}
		reqLocked = $req_locked_devices.indexOf(real_id(device?.id)) !== -1;
		if (unit === 'C' && value <= 4) {
			value_to_show = 'Lo';
		} else if (unit === 'C' && value >= 31) {
			value_to_show = 'Hi';
		} else if (unit === 'C' && value === 5) {
			value_to_show = 'fas fa-snowflake';
		} else if (unit === 'F' && value <= 40) {
			value_to_show = 'Lo';
		} else if (unit === 'F' && value === 41) {
			value_to_show = 'fas fa-snowflake';
		} else if (value >= 87) {
			value_to_show = 'Hi';
		} else {
			value_to_show = value + '°' + unit;
		}
	});

	const handleReq = async () => {
		let res;
		if (sendReq) {
			disabled = true;
			req_locked_devices.add(real_id(device.id));

			if (value <= 4 && !manual && unit !== 'F') {
				value = 4;
			} else if (value >= 31 && !manual && unit !== 'F') {
				value = 31;
			} else if (value <= 40 && !manual && unit === 'F') {
				value = 40;
			} else if (value >= 87 && !manual && unit === 'F') {
				value = 87;
			}

			if (manual) {
				//positioning control
				res = await gen1CloudReq(device, 'valve', { pos: value }, calibrated ? 20000 : 40000);
			} else if (windowOpen) {
				//Setting the desired temperature if window is opened
				res = await gen1CloudReq(
					device,
					'target_t',
					{
						enabled: windowOpen,
						value_op: value,
					},
					20000,
				);
			} else {
				//temperature control
				res = await gen1CloudReq(
					device,
					'target_t',
					{ value: value },
					calibrated ? 20000 : 40000,
				);
			}

			req_locked_devices.remove(real_id(device.id));

			if (res?.isok) {
				isLoading = false;
				disabled = false;
				cacheValue = value;
				// req_locked_devices.remove(real_id(device.id));
				if (manual) {
					settings.thermostats[0].pos = value;
					toast.open({
						type: 'success',
						title: $format('position_set_scc_toast', {
							values: { value: value + '%' },
							default: `Desired position was set to ${value}.`,
						}),
					});
				} else {
					settings.thermostats[0].target_t.value = value;
					toast.open({
						type: 'success',
						title: $format('temp_set_scc_toast', {
							values: { value: value + '°' + unit },
							default: `Desired temperature was set to ${value}.`,
						}),
					});
				}

				devices_settings.update_devices_settings(device.id, settings);
			} else {
				isLoading = false;
				disabled = false;
				// req_locked_devices.remove(real_id(device.id));
				value = cacheValue;
				toast.open({ type: 'danger', title: $format('error_global') });
			}
		}
	};

	/* time stuff */
	let timer;
	let interval;

	const setTime = (delay) => {
		clearTimeout(timer);
		timer = 0;
		timer = setTimeout(() => {
			sendReq = true;
			long = false;
			handleReq();
		}, delay);
	};

	const resetHideTimeout = () => {
		sendReq = false;
		long = true;
		setTime(2200);
	};

	const handleTemperatureChange = (val) => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			if (val === 0) {
				if (manual) {
					value = value - (value % 10);
					value -= 10;
				} else {
					if (unit === 'F') {
						value -= 1;
					} else {
						if (value <= 5) {
							value -= 1;
						} else {
							value -= 0.5;
							value = Number(value.toFixed(1));
						}
					}
				}

				if (!calibrated) {
					value = 4;
				}
			} else {
				if (manual) {
					value = value - (value % 10);
					value += 10;
				} else {
					if (unit === 'F') {
						value += 1;
					} else {
						if (value === 4) {
							value += 1;
						} else {
							value += 0.5;
							value = Number(value.toFixed(1));
						}
					}
				}
				if (!calibrated) {
					value = 31;
				}
			}
			if (value < 0 && manual) {
				value = 0;
			} else if (value > 100 && manual) {
				value = 100;
			}
			isLoading = true;
			if (value <= min && !manual) {
				value = min;
			} else if (value >= max && !manual) {
				value = max;
			}

			resetHideTimeout();
		} else {
			callbackValue = val;
			showModal = true;
		}
	};

	const handleLongPress = (val) => {
		if (!pinLocked) {
			interval = setInterval(() => {
				handleTemperatureChange(val);
			}, 100);
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleTemperatureChange(cacheValue);
	};
</script>

{#if useV2Components}
	<DoubleDeviceButtonV2
		id="doubledevicebutton-w5qunx7"
		aria-label={$format('button_group_temperature_change', {
			default: 'Handle temperature change ',
		})}
		{isLoading}
		disabled={disabled || !hasRights}
		class={`flex-shrink-0 ${className}`}
		{...devBtnsV2Props.doubleDeviceButtonV2}
	>
		<DeviceButtonV2
			id="devicebutton-5pziy0u"
			aria-label={$format('button_decrease_temperature', { default: 'Decrease temperature' })}
			slot="buttonLeft"
			icon="fas fa-minus"
			on:click={(e) => {
				e.stopPropagation();
				handleTemperatureChange(0);
			}}
			on:touchLongpress={(e) => {
				e.stopPropagation();
				handleLongPress(0);
			}}
			on:touchend={() => clearInterval(interval)}
			on:mouseup={() => {
				clearInterval(interval);
			}}
			{...devBtnsV2Props.deviceButtonV2}
		/>
		<svelte:fragment slot="content">
			{#if value_to_show.includes('fas')}
				<Icon icon={value_to_show} />
				<!-- {:else if windowOpen && value === cacheValue}
			<Icon icon={'fas fa-door-open'} /> -->
			{:else if value_to_show.includes('Hi') || value_to_show.includes('Lo')}
				<Text
					styleType={devBtnsV2Props?.deviceButtonV2?.size === 's'
						? 'body-extra-small'
						: 'controls-large'}
					class="text-bold"
				>
					{value_to_show}
				</Text>
			{:else}
				{#if windowOpen}
					<Icon size="xs" icon={'fas fa-door-open'} />
				{/if}
				<Text
					styleType={devBtnsV2Props?.deviceButtonV2?.size === 's'
						? 'body-extra-small'
						: 'controls-large'}
					class="text-bold"
				>
					{formatTargetValue(value)}
				</Text>
				{#if manual}
					<Text
						styleType={devBtnsV2Props?.deviceButtonV2?.size === 's'
							? 'tiny'
							: 'controls-small'}>{`${unit}`}</Text
					>
				{:else}
					<Text
						styleType={devBtnsV2Props?.deviceButtonV2?.size === 's'
							? 'tiny'
							: 'controls-small'}>{`°${unit}`}</Text
					>
				{/if}
			{/if}
		</svelte:fragment>
		<DeviceButtonV2
			id="devicebutton-ge3kb40"
			aria-label={$format('button_increase_temperature', { default: 'Increase temperature' })}
			slot="buttonRight"
			icon="fas fa-plus"
			variant="danger"
			on:click={(e) => {
				e.stopPropagation();
				handleTemperatureChange(1);
			}}
			on:touchLongpress={(e) => {
				e.stopPropagation();
				handleLongPress(1);
			}}
			on:touchend={() => clearInterval(interval)}
			on:mouseup={() => {
				clearInterval(interval);
			}}
			{...devBtnsV2Props.deviceButtonV2}
		/>
	</DoubleDeviceButtonV2>
{:else}
	<DoubleDeviceButton
		id="doubledevicebutton-w5qunx7"
		aria-label={$format('button_group_temperature_change', {
			default: 'Handle temperature change ',
		})}
		{isLoading}
		disabled={disabled || !hasRights}
		class={className}
	>
		<DeviceButton
			id="devicebutton-5pziy0u"
			aria-label={$format('button_decrease_temperature', { default: 'Decrease temperature' })}
			slot="buttonLeft"
			icon="fas fa-minus"
			on:click={(e) => {
				e.stopPropagation();
				handleTemperatureChange(0);
			}}
			on:touchLongpress={(e) => {
				e.stopPropagation();
				handleLongPress(0);
			}}
			on:touchend={() => clearInterval(interval)}
			on:mouseup={() => {
				clearInterval(interval);
			}}
		/>
		<svelte:fragment slot="content">
			{#if value_to_show.includes('fas')}
				<Icon icon={value_to_show} />
				<!-- {:else if windowOpen && value === cacheValue}
			<Icon icon={'fas fa-door-open'} /> -->
			{:else if value_to_show.includes('Hi') || value_to_show.includes('Lo')}
				<Text styleType="controls-large" class="text-bold">
					{value_to_show}
				</Text>
			{:else}
				{#if windowOpen}
					<Icon size="xs" icon={'fas fa-door-open'} />
				{/if}
				<Text styleType="controls-large" class="text-bold">
					{value}
				</Text>
				{#if manual}
					<Text styleType="controls-small">{`${unit}`}</Text>
				{:else}
					<Text styleType="controls-small">{`°${unit}`}</Text>
				{/if}
			{/if}
		</svelte:fragment>
		<DeviceButton
			id="devicebutton-ge3kb40"
			aria-label={$format('button_increase_temperature', { default: 'Increase temperature' })}
			slot="buttonRight"
			icon="fas fa-plus"
			variant="danger"
			on:click={(e) => {
				e.stopPropagation();
				handleTemperatureChange(1);
			}}
			on:touchLongpress={(e) => {
				e.stopPropagation();
				handleLongPress(1);
			}}
			on:touchend={() => clearInterval(interval)}
			on:mouseup={() => {
				clearInterval(interval);
			}}
		/>
	</DoubleDeviceButton>
{/if}
{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
